import React, { useState } from 'react';
import { DiscountModel } from '../../../models/superbit-api-models';
import { YES_NO_OPTIONS } from '../simulatorSettings.constants';
import { COMPANY_IDS } from '../companies.config';
import { AMOUNT_TYPE_OPTIONS, SHLOMO_DISCOUNT_TYPE_OPTIONS } from '../discounts.config';
import './DiscountForm.css';

interface DiscountFormProps {
    companyId: string; // lowercased
    /** DiscountModel from getDiscountForEdit (carries routesList + the row data) */
    model: any;
    saving: boolean;
    onSave: (model: any) => void;
    onCancel: () => void;
}

const num = (v: any) => (v === '' || v == null ? undefined : Number(v));

const DiscountForm: React.FC<DiscountFormProps> = ({ companyId, model, saving, onSave, onCancel }) => {
    const isAyalon = companyId === COMPANY_IDS.AYALON.toLowerCase();
    const isHarel = companyId === COMPANY_IDS.HAREL.toLowerCase();
    const isShlomo = companyId === COMPANY_IDS.SHLOMO.toLowerCase();
    const isOther = !isAyalon && !isHarel && !isShlomo;

    const [form, setForm] = useState<any>(() => ({
        name: model.name ?? '',
        sequenceId: model.sequenceId ?? '',
        autoActive: model.autoActive ?? 0,
        routes: model.routes ?? [],
        privateVehicle: !!model.privateVehicle,
        commercialVehicle: !!model.commercialVehicle,
        comprehensiveInsurance: !!model.comprehensiveInsurance,
        thirdPartyInsurance: !!model.thirdPartyInsurance,
        noPassedInsurance: model.noPassedInsurance ?? 0,
        sueExists: model.sueExists ?? 0,
        newDrivers: model.newDrivers ?? 0,
        driverAgeMin: model.driverAgeMin ?? '',
        driverAgeMax: model.driverAgeMax ?? '',
        ayalonHanachatSochen: model.ayalonHanachatSochen ?? '',
        ayalonHanachatHatama: model.ayalonHanachatHatama ?? '',
        sum: model.sum ?? '',
        collectiveNumber: model.collectiveNumber ?? '',
        shlomoDiscountType: model.shlomoDiscountType ?? 0,
        amountType: model.amountType ?? 1,
        feeParticipation: model.feeParticipation ?? '',
    }));

    const set = (field: string, value: any) => setForm((p: any) => ({ ...p, [field]: value }));

    const [routesError, setRoutesError] = useState(false);

    const routesOptions = model.routesList ?? []; // SelectListItem[]

    const onRoutesChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
        set('routes', selected);
        if (selected.length > 0) setRoutesError(false);
    };

    const submit = () => {
        // מסלול הוא שדה חובה — הנחה ללא מסלול לא נשמרת (SCRUM-1725)
        if (!form.routes || form.routes.length === 0) {
            setRoutesError(true);
            return;
        }
        const base = model.toJSON ? model.toJSON() : {};
        const payload = DiscountModel.fromJS({
            ...base,
            name: form.name,
            sequenceId: num(form.sequenceId),
            autoActive: Number(form.autoActive),
            routes: form.routes,
            privateVehicle: form.privateVehicle,
            commercialVehicle: form.commercialVehicle,
            comprehensiveInsurance: form.comprehensiveInsurance,
            thirdPartyInsurance: form.thirdPartyInsurance,
            noPassedInsurance: Number(form.noPassedInsurance),
            sueExists: Number(form.sueExists),
            newDrivers: Number(form.newDrivers),
            driverAgeMin: num(form.driverAgeMin),
            driverAgeMax: num(form.driverAgeMax),
            ...(isAyalon && {
                amountType: 1,
                ayalonHanachatSochen: num(form.ayalonHanachatSochen),
                ayalonHanachatHatama: num(form.ayalonHanachatHatama),
            }),
            ...(isHarel && {
                amountType: 1,
                sum: num(form.sum),
                collectiveNumber: num(form.collectiveNumber),
            }),
            ...(isShlomo && {
                shlomoDiscountType: Number(form.shlomoDiscountType),
                sum: num(form.sum),
                amountType: Number(form.amountType),
                feeParticipation: num(form.feeParticipation),
            }),
            ...(isOther && {
                sum: num(form.sum),
                amountType: Number(form.amountType),
                feeParticipation: num(form.feeParticipation),
            }),
        });
        onSave(payload);
    };

    const yesNoSelect = (field: string, label: string) => (
        <div className="sbsim-dform__field">
            <label className="sbsim-dform__label">{label}</label>
            <select
                className="sbsim-dform__select"
                value={form[field]}
                onChange={(e) => set(field, Number(e.target.value))}
            >
                {YES_NO_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                        {o.label}
                    </option>
                ))}
            </select>
        </div>
    );

    return (
        <div className="sbsim-dform">
            <div className="sbsim-dform__title">{model.id ? 'עריכת הנחה' : 'הנחה חדשה'}</div>

            <div className="sbsim-dform__grid">
                <div className="sbsim-dform__field">
                    <label className="sbsim-dform__label">שם ההנחה</label>
                    <input
                        className="sbsim-dform__input"
                        value={form.name}
                        onChange={(e) => set('name', e.target.value)}
                    />
                </div>

                <div className="sbsim-dform__field">
                    <label className="sbsim-dform__label">מספר סידורי</label>
                    <input
                        className="sbsim-dform__input"
                        type="number"
                        value={form.sequenceId}
                        onChange={(e) => set('sequenceId', e.target.value)}
                    />
                </div>

                {yesNoSelect('autoActive', 'הפעלת הנחה באופן אוטומטי')}

                <div className="sbsim-dform__field sbsim-dform__field--wide">
                    <label className="sbsim-dform__label">
                        מסלולים <span className="sbsim-dform__req">*</span>
                    </label>
                    <select
                        className={
                            'sbsim-dform__select sbsim-dform__multi' +
                            (routesError ? ' sbsim-dform__select--error' : '')
                        }
                        multiple
                        value={form.routes}
                        onChange={onRoutesChange}
                        aria-invalid={routesError}
                    >
                        {routesOptions.map((o: any) => (
                            <option key={o.value} value={o.value}>
                                {o.text}
                            </option>
                        ))}
                    </select>
                    {routesError && (
                        <span className="sbsim-dform__error">יש לבחור מסלול אחד לפחות</span>
                    )}
                </div>

                {/* company-specific */}
                {isAyalon && (
                    <>
                        <div className="sbsim-dform__field">
                            <label className="sbsim-dform__label">הנחת מנהל %</label>
                            <input
                                className="sbsim-dform__input"
                                type="number"
                                value={form.ayalonHanachatSochen}
                                onChange={(e) => set('ayalonHanachatSochen', e.target.value)}
                            />
                        </div>
                        <div className="sbsim-dform__field">
                            <label className="sbsim-dform__label">הנחת מחשבון %</label>
                            <input
                                className="sbsim-dform__input"
                                type="number"
                                value={form.ayalonHanachatHatama}
                                onChange={(e) => set('ayalonHanachatHatama', e.target.value)}
                            />
                        </div>
                    </>
                )}

                {isHarel && (
                    <>
                        <div className="sbsim-dform__field">
                            <label className="sbsim-dform__label">הנחה משותפת (על חשבון עמלה) %</label>
                            <input
                                className="sbsim-dform__input"
                                type="number"
                                value={form.sum}
                                onChange={(e) => set('sum', e.target.value)}
                            />
                        </div>
                        <div className="sbsim-dform__field">
                            <label className="sbsim-dform__label">קולקטיב</label>
                            <input
                                className="sbsim-dform__input"
                                type="number"
                                value={form.collectiveNumber}
                                onChange={(e) => set('collectiveNumber', e.target.value)}
                            />
                        </div>
                    </>
                )}

                {isShlomo && (
                    <>
                        <div className="sbsim-dform__field">
                            <label className="sbsim-dform__label">סוג הנחה</label>
                            <select
                                className="sbsim-dform__select"
                                value={form.shlomoDiscountType}
                                onChange={(e) => set('shlomoDiscountType', Number(e.target.value))}
                            >
                                {SHLOMO_DISCOUNT_TYPE_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="sbsim-dform__field">
                            <label className="sbsim-dform__label">גובה ההנחה</label>
                            <input
                                className="sbsim-dform__input"
                                type="number"
                                value={form.sum}
                                onChange={(e) => set('sum', e.target.value)}
                            />
                        </div>
                        <div className="sbsim-dform__field">
                            <label className="sbsim-dform__label">סוג סכום</label>
                            <select
                                className="sbsim-dform__select"
                                value={form.amountType}
                                onChange={(e) => set('amountType', Number(e.target.value))}
                            >
                                {AMOUNT_TYPE_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="sbsim-dform__field">
                            <label className="sbsim-dform__label">השתתפות בעמלה %</label>
                            <input
                                className="sbsim-dform__input"
                                type="number"
                                value={form.feeParticipation}
                                onChange={(e) => set('feeParticipation', e.target.value)}
                            />
                        </div>
                    </>
                )}

                {isOther && (
                    <>
                        <div className="sbsim-dform__field">
                            <label className="sbsim-dform__label">גובה ההנחה</label>
                            <input
                                className="sbsim-dform__input"
                                type="number"
                                value={form.sum}
                                onChange={(e) => set('sum', e.target.value)}
                            />
                        </div>
                        <div className="sbsim-dform__field">
                            <label className="sbsim-dform__label">סוג סכום</label>
                            <select
                                className="sbsim-dform__select"
                                value={form.amountType}
                                onChange={(e) => set('amountType', Number(e.target.value))}
                            >
                                {AMOUNT_TYPE_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="sbsim-dform__field">
                            <label className="sbsim-dform__label">השתתפות בעמלה %</label>
                            <input
                                className="sbsim-dform__input"
                                type="number"
                                value={form.feeParticipation}
                                onChange={(e) => set('feeParticipation', e.target.value)}
                            />
                        </div>
                    </>
                )}

                {yesNoSelect('noPassedInsurance', 'תקפה לנהגים ללא עבר ביטוחי?')}
                {yesNoSelect('sueExists', 'תקפה אם קיימת תביעה?')}
                {yesNoSelect('newDrivers', 'תקפה לנהגים חדשים?')}

                <div className="sbsim-dform__field">
                    <label className="sbsim-dform__label">גיל נהגים בהנחה</label>
                    <div className="sbsim-dform__range">
                        <input
                            className="sbsim-dform__input"
                            type="number"
                            value={form.driverAgeMin}
                            onChange={(e) => set('driverAgeMin', e.target.value)}
                        />
                        <span className="sbsim-dform__dash">-</span>
                        <input
                            className="sbsim-dform__input"
                            type="number"
                            value={form.driverAgeMax}
                            onChange={(e) => set('driverAgeMax', e.target.value)}
                        />
                    </div>
                </div>

                <div className="sbsim-dform__field">
                    <label className="sbsim-dform__label">תקפה לרכב מסוג</label>
                    <div className="sbsim-dform__checks">
                        <label className="sbsim-dform__check">
                            <input
                                type="checkbox"
                                checked={form.privateVehicle}
                                onChange={(e) => set('privateVehicle', e.target.checked)}
                            />
                            פרטי
                        </label>
                        <label className="sbsim-dform__check">
                            <input
                                type="checkbox"
                                checked={form.commercialVehicle}
                                onChange={(e) => set('commercialVehicle', e.target.checked)}
                            />
                            מסחרי
                        </label>
                    </div>
                </div>

                <div className="sbsim-dform__field">
                    <label className="sbsim-dform__label">תקפה לסוג ביטוח</label>
                    <div className="sbsim-dform__checks">
                        <label className="sbsim-dform__check">
                            <input
                                type="checkbox"
                                checked={form.comprehensiveInsurance}
                                onChange={(e) => set('comprehensiveInsurance', e.target.checked)}
                            />
                            מקיף
                        </label>
                        <label className="sbsim-dform__check">
                            <input
                                type="checkbox"
                                checked={form.thirdPartyInsurance}
                                onChange={(e) => set('thirdPartyInsurance', e.target.checked)}
                            />
                            צד ג'
                        </label>
                    </div>
                </div>
            </div>

            <div className="sbsim-dform__actions">
                <button
                    type="button"
                    className="sbsim-dform__save"
                    onClick={submit}
                    disabled={saving}
                >
                    {saving ? 'שומר...' : 'שמור'}
                </button>
                <button
                    type="button"
                    className="sbsim-dform__cancel"
                    onClick={onCancel}
                    disabled={saving}
                >
                    ביטול
                </button>
            </div>
        </div>
    );
};

export default DiscountForm;
